'use client'

import React, { useState, useMemo, useEffect } from 'react'
import { JobBoardVolunteer, JobBoardTeam } from '../../api/work-orders.api'
import { usePriorityData } from '@/features/priority/hooks/use-priority-data'
import { Input } from '@/shared/components/ui/input'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Search,
  UserCheck,
  Star,
  Tag,
  X,
  Plus,
  CheckCircle2,
  Layers,
  Users,
  PanelRightClose,
  PanelRightOpen,
  SlidersHorizontal,
  Maximize2,
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { setActiveJobBoardDrag } from '../../constants/job-board-dnd'

interface JobBoardSideDrawerProps {
  isOpen: boolean
  onToggle: () => void
  regionId?: number
  volunteers: JobBoardVolunteer[]
  candidateTeamLeads: JobBoardVolunteer[]
  activeLeaderIds?: number[]
  teams?: JobBoardTeam[]
  onToggleLeader?: (volunteer: JobBoardVolunteer) => void
  onAssignToTeam?: (volunteerId: number, targetTeamId: number | null) => void
  onScheduleWorkScope: (item: any) => void
  onExpandMode?: () => void
}

export function JobBoardSideDrawer({
  isOpen,
  onToggle,
  regionId,
  volunteers,
  candidateTeamLeads,
  activeLeaderIds = [],
  teams = [],
  onToggleLeader,
  onAssignToTeam,
  onScheduleWorkScope,
  onExpandMode,
}: JobBoardSideDrawerProps) {
  const [activeTab, setActiveTab] = useState<'backlog' | 'volunteers'>('backlog')

  // --- Priority Matrix (Backlog) State ---
  const { data: priorityHomes, isLoading: isLoadingPriority } = usePriorityData(regionId)
  const [backlogSearch, setBacklogSearch] = useState('')
  const [includeGoodwill, setIncludeGoodwill] = useState(true)

  // Flatten homes into ready work scopes
  const readyWorkScopes = useMemo(() => {
    if (!priorityHomes || !Array.isArray(priorityHomes)) return []

    const items: Array<{
      homeId: number
      homeownerName: string
      address: string
      workScope: string
      workScopeDisplay: string
      workScopeId: number | null
      status: string
      isGoodwill?: boolean
      priorityRank?: number
    }> = []

    priorityHomes.forEach((entry: any) => {
      // Format A: WorkScopePriorityDisplay (from GET /priority)
      if (entry.priorities && (Array.isArray(entry.priorities.ready) || Array.isArray(entry.priorities.goodwill))) {
        const wsSlug = entry.workScope || 'Work Scope'
        const wsDisplay = entry.workScopeDisplay || entry.workScope || 'Work Order'
        const wsId = entry.workScopeId || null

        // Standard ready scopes
        ;(entry.priorities.ready || []).forEach((item: any) => {
          items.push({
            homeId: item.homeId,
            homeownerName: item.name || 'Homeowner',
            address: item.address || '',
            workScope: wsSlug,
            workScopeDisplay: wsDisplay,
            workScopeId: wsId,
            status: item.status || 'Ready',
            isGoodwill: false,
          })
        })

        // Goodwill scopes
        ;(entry.priorities.goodwill || []).forEach((item: any) => {
          if (item.status === 'Ready' || !item.status) {
            items.push({
              homeId: item.homeId,
              homeownerName: item.name || 'Homeowner',
              address: item.address || '',
              workScope: wsSlug,
              workScopeDisplay: wsDisplay,
              workScopeId: wsId,
              status: item.status || 'Ready',
              isGoodwill: true,
            })
          }
        })
        return
      }

      // Format B: Home entity with homeWorkScopes
      const home = entry
      const homeownerName =
        home.homeowner?.user?.fullname || home.homeowner?.name || 'Homeowner'
      const address = home.address || ''

      if (Array.isArray(home.homeWorkScopes)) {
        home.homeWorkScopes.forEach((hws: any) => {
          const status = (hws.status || '').toLowerCase()
          // Only show scopes that are Ready / In Progress / Scheduled
          if (
            status === 'ready' ||
            status === 'in_progress' ||
            status === 'in progress' ||
            status === 'pending' ||
            status === 'approved' ||
            !status
          ) {
            items.push({
              homeId: home.id,
              homeownerName,
              address,
              workScope: hws.workScope?.workScope || hws.workScopeName || 'Work Scope',
              workScopeDisplay:
                hws.workScope?.workScopeDisplay ||
                hws.workScope?.workScope ||
                hws.workScopeName ||
                'Work Order',
              workScopeId: hws.workScope?.id || null,
              status: hws.status || 'Ready',
              isGoodwill: hws.isGoodwill || false,
            })
          }
        })
      }
    })

    return items
  }, [priorityHomes])

  const filteredBacklogItems = useMemo(() => {
    return readyWorkScopes.filter((item) => {
      if (!includeGoodwill && item.isGoodwill) return false
      if (backlogSearch.trim()) {
        const q = backlogSearch.toLowerCase()
        const matchName = item.homeownerName.toLowerCase().includes(q)
        const matchScope = item.workScopeDisplay.toLowerCase().includes(q)
        const matchAddress = item.address.toLowerCase().includes(q)
        return matchName || matchScope || matchAddress
      }
      return true
    })
  }, [readyWorkScopes, backlogSearch, includeGoodwill])

  // --- Volunteers State ---
  const [volSearch, setVolSearch] = useState('')
  const [filterCandidateOnly, setFilterCandidateOnly] = useState(false)
  const [localTags, setLocalTags] = useState<Record<number, string[]>>({})
  const [activeTagInputId, setActiveTagInputId] = useState<number | null>(null)
  const [newTagText, setNewTagText] = useState('')

  useEffect(() => {
    try {
      const saved = localStorage.getItem('job_board_planner_volunteer_tags')
      if (saved) {
        setLocalTags(JSON.parse(saved))
      }
    } catch {}
  }, [])

  const saveTags = (updated: Record<number, string[]>) => {
    setLocalTags(updated)
    try {
      localStorage.setItem('job_board_planner_volunteer_tags', JSON.stringify(updated))
    } catch {}
  }

  const addTag = (userId: number, tag: string) => {
    const trimmed = tag.trim()
    if (!trimmed) return
    const current = localTags[userId] || []
    if (!current.includes(trimmed)) {
      const next = { ...localTags, [userId]: [...current, trimmed] }
      saveTags(next)
    }
    setNewTagText('')
    setActiveTagInputId(null)
  }

  const removeTag = (userId: number, tagToRemove: string) => {
    const current = localTags[userId] || []
    const next = {
      ...localTags,
      [userId]: current.filter((t) => t !== tagToRemove),
    }
    saveTags(next)
  }

  const [teamFilter, setTeamFilter] = useState<string>('ALL')

  const candidateIds = new Set(candidateTeamLeads.map((c) => c.userId))
  const activeIdsSet = new Set(activeLeaderIds)

  const filteredVolunteers = volunteers.filter((vol) => {
    if (filterCandidateOnly && !candidateIds.has(vol.userId)) {
      return false
    }
    if (teamFilter === 'UNASSIGNED') {
      if (vol.teamId) return false
    } else if (teamFilter !== 'ALL') {
      if (String(vol.teamId) !== teamFilter) return false
    }
    if (volSearch.trim()) {
      const q = volSearch.toLowerCase()
      const matchesName = vol.fullName.toLowerCase().includes(q)
      const matchesGroup = vol.groupName?.toLowerCase().includes(q)
      const tags = localTags[vol.userId] || []
      const matchesTag = tags.some((t) => t.toLowerCase().includes(q))
      return matchesName || matchesGroup || matchesTag
    }
    return true
  })

  if (!isOpen) {
    return null
  }

  return (
    <div className="w-80 lg:w-96 border-l bg-card flex flex-col h-full shadow-2xl z-30 shrink-0 select-none">
      {/* Drawer Top Bar */}
      <div className="p-3 border-b flex items-center justify-between bg-muted/40">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-primary" />
          <span className="font-bold text-sm text-foreground">Planning Drawer</span>
        </div>

        <div className="flex items-center gap-1">
          {onExpandMode && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onExpandMode}
              className="h-7 w-7 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground"
              title="Expand to Full Working Area"
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </Button>
          )}

          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className="h-7 w-7 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground"
            title="Close Drawer"
          >
            <PanelRightClose className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Drawer Navigation Tabs */}
      <div className="grid grid-cols-2 p-1.5 border-b bg-muted/20 gap-1 text-xs">
        <button
          type="button"
          onClick={() => setActiveTab('backlog')}
          className={cn(
            'flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md font-semibold transition-all',
            activeTab === 'backlog'
              ? 'bg-background text-primary shadow-sm'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          )}
        >
          <Layers className="w-3.5 h-3.5" />
          <span>Priority Matrix</span>
          <Badge
            variant="secondary"
            className="text-[9px] px-1 py-0 h-4 bg-muted text-muted-foreground"
          >
            {filteredBacklogItems.length}
          </Badge>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('volunteers')}
          className={cn(
            'flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md font-semibold transition-all',
            activeTab === 'volunteers'
              ? 'bg-background text-primary shadow-sm'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          )}
        >
          <Users className="w-3.5 h-3.5" />
          <span>Volunteers</span>
          <Badge
            variant="secondary"
            className="text-[9px] px-1 py-0 h-4 bg-muted text-muted-foreground"
          >
            {volunteers.length}
          </Badge>
        </button>
      </div>

      {/* Tab 1: Priority Matrix (Ready Backlog) */}
      {activeTab === 'backlog' && (
        <div className="flex-1 flex flex-col min-h-0">
          {/* Filter / Search Bar */}
          <div className="p-3 border-b space-y-2 bg-background">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={backlogSearch}
                onChange={(e) => setBacklogSearch((e.target as HTMLInputElement).value)}
                placeholder="Search homes, scopes, addresses..."
                className="h-8 pl-8 text-xs"
              />
            </div>

            <div className="flex items-center justify-between text-[11px]">
              <label className="flex items-center gap-1.5 cursor-pointer text-muted-foreground hover:text-foreground">
                <input
                  type="checkbox"
                  checked={includeGoodwill}
                  onChange={(e) => setIncludeGoodwill(e.target.checked)}
                  className="rounded text-primary focus:ring-0 w-3.5 h-3.5"
                />
                <span>Include Goodwill Scopes</span>
              </label>
              <span className="text-[10px] text-muted-foreground italic">Drag or click to add</span>
            </div>
          </div>

          {/* List of ready jobs */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {isLoadingPriority ? (
              <div className="text-center py-12 text-xs text-muted-foreground animate-pulse">
                Loading ready work scopes...
              </div>
            ) : filteredBacklogItems.length === 0 ? (
              <div className="text-center py-12 text-xs text-muted-foreground">
                No ready work scopes found matching criteria
              </div>
            ) : (
              filteredBacklogItems.map((item, idx) => (
                <div
                  key={`${item.homeId}-${item.workScope}-${idx}`}
                  draggable
                  onDragStart={(e) => {
                    const payload = { type: 'NEW_WORK_SCOPE' as const, scope: item }
                    setActiveJobBoardDrag(payload)
                    e.dataTransfer.setData('text/plain', JSON.stringify(payload))
                    e.dataTransfer.effectAllowed = 'copy'
                  }}
                  onDragEnd={() => {
                    setActiveJobBoardDrag(null)
                  }}
                  className={cn(
                    'p-2.5 rounded-xl border text-xs bg-background hover:border-indigo-400 transition-all shadow-sm flex flex-col justify-between gap-2 cursor-grab active:cursor-grabbing',
                    item.isGoodwill && 'border-green-300 bg-green-50/20'
                  )}
                >
                  <div>
                    <div className="flex items-start justify-between gap-1">
                      <div className="font-semibold text-foreground truncate">
                        {item.homeownerName}
                      </div>
                      <Badge
                        variant="outline"
                        className="text-[10px] bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300 border-indigo-200 shrink-0 font-medium"
                      >
                        {item.workScopeDisplay}
                      </Badge>
                    </div>
                    {item.address && (
                      <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                        {item.address}
                      </div>
                    )}
                    {item.isGoodwill && (
                      <span className="text-[10px] text-green-700 font-medium">
                        Goodwill Addition
                      </span>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-1.5 border-t">
                    <span className="text-[10px] text-emerald-600 font-medium flex items-center gap-0.5">
                      <CheckCircle2 className="w-3 h-3" />
                      Ready to Schedule
                    </span>
                    <Button
                      size="sm"
                      onClick={() => onScheduleWorkScope(item)}
                      className="h-6 text-[11px] px-2 bg-indigo-600 hover:bg-indigo-700 text-white gap-1 font-medium shadow-sm"
                    >
                      <Plus className="w-3 h-3" />
                      Add to Board
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Tab 2: Volunteers & Team Leaders */}
      {activeTab === 'volunteers' && (
        <div className="flex-1 flex flex-col min-h-0">
          {/* Filter / Search Bar */}
          <div className="p-3 border-b space-y-2 bg-background">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={volSearch}
                onChange={(e) => setVolSearch((e.target as HTMLInputElement).value)}
                placeholder="Search volunteers, groups, tags..."
                className="h-8 pl-8 text-xs"
              />
            </div>
            <div className="flex gap-1.5 items-center">
              <Button
                size="sm"
                variant={filterCandidateOnly ? 'default' : 'outline'}
                onClick={() => setFilterCandidateOnly(!filterCandidateOnly)}
                className="h-7 text-xs flex-1 gap-1 font-medium"
              >
                <Star className="w-3 h-3 text-amber-500" />
                Team Leads ({candidateTeamLeads.length})
              </Button>

              {teams.length > 0 && (
                <select
                  value={teamFilter}
                  onChange={(e) => setTeamFilter(e.target.value)}
                  className="h-7 text-[11px] px-2 rounded-md border border-input bg-background font-medium focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value="ALL">All Teams</option>
                  <option value="UNASSIGNED">No Team Assigned</option>
                  {teams.map((t) => (
                    <option key={t.id} value={String(t.id)}>
                      {t.teamName}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Volunteer List */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {filteredVolunteers.length === 0 ? (
              <div className="text-center py-12 text-xs text-muted-foreground">
                No volunteers found matching search
              </div>
            ) : (
              filteredVolunteers.map((vol) => {
                const isCandidate = candidateIds.has(vol.userId)
                const isActiveOnBoard = activeIdsSet.has(vol.userId)
                const tags = localTags[vol.userId] || []

                // Extract top rated skills (rating >= 3)
                const topSkills: Array<{ name: string; rating: number }> = []
                if (vol.skills) {
                  const s = vol.skills
                  if (s.drywallFinishing && s.drywallFinishing >= 3)
                    topSkills.push({ name: 'Drywall', rating: s.drywallFinishing })
                  if (s.roughCarpentry && s.roughCarpentry >= 3)
                    topSkills.push({ name: 'Carpentry', rating: s.roughCarpentry })
                  if (s.finishCarpentry && s.finishCarpentry >= 3)
                    topSkills.push({ name: 'Finish Carpentry', rating: s.finishCarpentry })
                  if (s.flooring && s.flooring >= 3)
                    topSkills.push({ name: 'Flooring', rating: s.flooring })
                  if (s.woodWorking && s.woodWorking >= 3)
                    topSkills.push({ name: 'Woodwork', rating: s.woodWorking })
                  if (s.electrical && s.electrical >= 3)
                    topSkills.push({ name: 'Electrical', rating: s.electrical })
                  if (s.plumbing && s.plumbing >= 3)
                    topSkills.push({ name: 'Plumbing', rating: s.plumbing })
                  if (s.hvac && s.hvac >= 3)
                    topSkills.push({ name: 'HVAC', rating: s.hvac })
                  if (s.roofing && s.roofing >= 3)
                    topSkills.push({ name: 'Roofing', rating: s.roofing })
                }

                return (
                  <div
                    key={vol.userId}
                    className={cn(
                      'p-2.5 rounded-xl border text-xs bg-background hover:border-primary/50 transition-all shadow-sm flex flex-col justify-between gap-1.5',
                      isActiveOnBoard && 'border-emerald-300 bg-emerald-50/15 dark:bg-emerald-950/20',
                      isCandidate && !isActiveOnBoard && 'border-amber-200 bg-amber-50/10'
                    )}
                  >
                    {/* Volunteer Header */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-semibold text-foreground truncate">
                            {vol.fullName}
                          </span>
                          {isCandidate && (
                            <Badge
                              variant="outline"
                              className="text-[9px] px-1 py-0 bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300 border-amber-300 gap-0.5 font-medium"
                            >
                              <Star className="w-2.5 h-2.5 fill-amber-500 text-amber-500" />
                              Lead Rating {vol.experienceLeadingTeams}/5
                            </Badge>
                          )}
                        </div>
                        {vol.groupName && (
                          <div className="text-[11px] text-muted-foreground truncate">
                            {vol.groupName}
                          </div>
                        )}
                      </div>

                      {onToggleLeader && (
                        <Button
                          size="sm"
                          variant={isActiveOnBoard ? 'outline' : 'default'}
                          onClick={() => onToggleLeader(vol)}
                          className={cn(
                            'h-6 text-[11px] px-2 shrink-0 gap-1 font-medium',
                            isActiveOnBoard
                              ? 'border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950 dark:text-emerald-300'
                              : 'bg-primary/10 text-primary hover:bg-primary hover:text-white border border-primary/20'
                          )}
                          title={
                            isActiveOnBoard
                              ? 'Leader track is active on the Job Board'
                              : 'Add this leader to the Job Board'
                          }
                        >
                          {isActiveOnBoard ? (
                            <>
                              <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                              On Board
                            </>
                          ) : (
                            <>
                              <Plus className="w-3 h-3" />
                              Add Leader
                            </>
                          )}
                        </Button>
                      )}
                    </div>

                    {/* Team Assignment Selector */}
                    {teams.length > 0 && onAssignToTeam && (
                      <div className="flex items-center justify-between gap-1.5 bg-muted/40 p-1.5 rounded-lg">
                        <span className="text-[10px] text-muted-foreground font-medium flex items-center gap-1">
                          <Users className="w-3 h-3 text-muted-foreground" /> Team:
                        </span>
                        <select
                          value={vol.teamId ? String(vol.teamId) : ''}
                          onChange={(e) => {
                            const val = e.target.value ? Number(e.target.value) : null
                            onAssignToTeam(vol.userId, val)
                          }}
                          className="h-6 text-[10px] px-1.5 rounded border border-input bg-background font-medium focus:outline-none focus:ring-1 focus:ring-ring max-w-[160px] truncate"
                        >
                          <option value="">No Team (Unassigned)</option>
                          {teams.map((t) => (
                            <option key={t.id} value={String(t.id)}>
                              {t.teamName}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Skills Badges */}
                    {topSkills.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {topSkills.map((s) => (
                          <span
                            key={s.name}
                            className="bg-muted px-1.5 py-0.5 rounded text-[10px] text-muted-foreground font-medium"
                          >
                            {s.name} {s.rating}/5
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Scratchpad Planner Tags */}
                    <div className="mt-1.5 pt-1.5 border-t flex flex-wrap items-center gap-1">
                      {tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-0.5 bg-primary/10 text-primary px-1.5 py-0.5 rounded text-[10px] font-medium"
                        >
                          {tag}
                          <button
                            onClick={() => removeTag(vol.userId, tag)}
                            className="hover:text-destructive ml-0.5"
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </span>
                      ))}

                      {activeTagInputId === vol.userId ? (
                        <div className="inline-flex items-center gap-1">
                          <input
                            type="text"
                            value={newTagText}
                            onChange={(e) =>
                              setNewTagText((e.target as HTMLInputElement).value)
                            }
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') addTag(vol.userId, newTagText)
                              if (e.key === 'Escape') setActiveTagInputId(null)
                            }}
                            placeholder="Tag name..."
                            className="h-5 w-20 text-[10px] px-1 border rounded bg-background"
                            autoFocus
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => addTag(vol.userId, newTagText)}
                            className="h-5 px-1 text-[10px]"
                          >
                            Add
                          </Button>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setActiveTagInputId(vol.userId)
                            setNewTagText('')
                          }}
                          className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-0.5 py-0.5 px-1 rounded hover:bg-muted"
                        >
                          <Tag className="w-2.5 h-2.5" />
                          + Tag
                        </button>
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}
